/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description utils for era
 */

import _ from 'lodash';
import { JsonTree } from '@react-awesome-query-builder/antd';
import { FieldTypeConfigs } from 'components/forms/schema/claimsAdvancedFilterSchema';
import { QUERY_BUILDER_FILTER_TYPE } from 'lib/SearchQueryBuilder/config/entity.config';
import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';
import { getWorklistRule } from 'hooks/claim/useAllClaimStatusRequests';
import { addRuleToQuery, prepareQueryRule } from 'utils/queryBuilder';
import { getQueryBuilderSchemaForAttributes } from 'utils/admin/customAttributes';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { CLAIM_FREQUENCY_OPTIONS, CLAIM_WORK_STATES, GROUPED_CLAIM_WORK_STATES } from 'app-constants/claims/claimRequest';
import {
	ERA_CLAIM_FILTER_FIELDS,
	ERA_FILTER_FIELDS,
	PAYMENT_RECONCILIATION_OUTCOME,
	PAYMENT_RECONCILIATION_STATUS,
	RECONCILED_STATUS_OPTIONS,
} from 'app-constants/claims/era';

// filter data keys
const filterDataKeys = {
	eraNumber: 'eraNumber',
	method: 'method',
	amount: 'amount',
	paymentDate: 'date',
	payerName: 'payerName',
	outcome: 'outcome',
	workState: 'workState',
	claimNumber: 'claimNumber',
};

// era filter schema
const getEraFilterSchema = (customAttributes?: CustomAttributes.IAllResponse[], tenantPayers?: Payers.ITenantPayer[]): AnyType[] => {
	const customAttributesFilterSchema = _.compact(_.map(customAttributes, (attribute) => getQueryBuilderSchemaForAttributes(attribute)));
	return [
		...customAttributesFilterSchema,
		{
			...FieldTypeConfigs.multiselect,
			field: filterDataKeys.payerName,
			display_name: ERA_FILTER_FIELDS.payerName.label,
			isSearchable: true,
			options: _.map(tenantPayers || [], (payer) => ({ value: payer?.id, label: payer?.name })),
			custom_props: {
				placeholder: ERA_FILTER_FIELDS.payerName.placeholder,
			},
		},
		{
			...FieldTypeConfigs.text,
			field: filterDataKeys.method,
			display_name: ERA_FILTER_FIELDS.paymentMethod.label,
			isSearchable: true,
			custom_props: {
				placeholder: ERA_FILTER_FIELDS.paymentMethod.placeholder,
			},
		},
		{
			...FieldTypeConfigs.rangePicker,
			field: filterDataKeys.paymentDate,
			operators: [OPERATORS.between],
			display_name: ERA_FILTER_FIELDS.paymentDate.label,
			isSearchable: true,
			custom_props: {
				placeholder: ERA_FILTER_FIELDS.paymentDate.placeholder,
			},
		},
		{
			...FieldTypeConfigs.text,
			field: filterDataKeys.claimNumber,
			isSearchable: true,
			display_name: ERA_FILTER_FIELDS.claimNumber.label,
			custom_props: {
				placeholder: ERA_FILTER_FIELDS.claimNumber.placeholder,
			},
		},
		{
			ui_field_type: QUERY_BUILDER_FILTER_TYPE.select,
			searchOperator: OPERATORS.equal,
			operators: [OPERATORS.equal],
			field: filterDataKeys.outcome,
			display_name: ERA_FILTER_FIELDS.reconciledStatus.label,
			isSearchable: true,
			options: RECONCILED_STATUS_OPTIONS,
			custom_props: {
				placeholder: ERA_FILTER_FIELDS.reconciledStatus.placeholder,
			},
		},
		{
			...FieldTypeConfigs.minMaxRange,
			field: filterDataKeys.amount,
			display_name: ERA_FILTER_FIELDS.amount.label,
			isSearchable: true,
			custom_props: {
				prefix: '$',
				placeholder: ERA_FILTER_FIELDS.amount.placeholder,
				stringifiedValueRenderer: (value: number) => `$${value}`,
			},
		},
		{
			...FieldTypeConfigs.text,
			field: filterDataKeys.eraNumber,
			display_name: ERA_FILTER_FIELDS.eraNumber.label,
			isDefaultSelectedField: true,
			isSearchable: true,
			custom_props: {
				placeholder: ERA_FILTER_FIELDS.eraNumber.placeholder,
			},
		},
	];
};

// get provider adjustment meta
const getProviderAdjustmentMeta = (data: Fhir.PaymentReconciliationAllocation[]): ERA.IProviderAdjustmentMeta[] => {
	const dataItem = [];
	_.map(data, (allocation) => {
		const isPlbSection = allocation?.identifier?.system === fhirSystemMapping?.providerAdjustmentReferenceIdentifier;
		if (isPlbSection) {
			const extensions = allocation?.extension || [];
			_.map(extensions, (extension) => {
				const plbSystem = extension?.url === fhirSystemMapping?.plb;
				if (plbSystem) {
					dataItem.push({
						identifier: allocation?.payee?.identifier?.value,
						amount: allocation?.amount?.value,
						date: _.find(extension?.extension, { url: 'FiscalPeriodDate' })?.valueDate,
						reason: _.find(extension?.extension, { url: 'AdjustmentReason' })?.valueCoding?.code,
						description: _.find(extension?.extension, { url: 'AdjustmentReason' })?.valueCoding?.display,
					});
				}
			});
		}
	});
	return dataItem;
};

// era claim filter data keys
const filterEraClaimDataKeys = {
	claimAmount: 'claimAmount',
	claimFrequencyCode: 'claimFrequencyCode',
	claimNumber: 'claimNumber',
	patientName: 'patientName',
	patientId: 'patientId',
};

// era claim filter schema
const getEraClaimFilterSchema = (): AnyType[] => {
	return [
		{
			...FieldTypeConfigs.text,
			field: filterEraClaimDataKeys.claimNumber,
			display_name: ERA_CLAIM_FILTER_FIELDS.claimNumber.label,
			isSearchable: true,
			custom_props: {
				placeholder: ERA_CLAIM_FILTER_FIELDS.claimNumber.placeholder,
			},
		},
		{
			...FieldTypeConfigs.multiselect,
			field: filterEraClaimDataKeys.claimFrequencyCode,
			display_name: ERA_CLAIM_FILTER_FIELDS.claimFrequency.label,
			isSearchable: true,
			options: CLAIM_FREQUENCY_OPTIONS,
			custom_props: {
				placeholder: ERA_CLAIM_FILTER_FIELDS.claimFrequency.placeholder,
			},
		},
		{
			...FieldTypeConfigs.text,
			field: filterEraClaimDataKeys.patientName,
			display_name: ERA_CLAIM_FILTER_FIELDS.patientName.label,
			isSearchable: true,
			custom_props: {
				placeholder: ERA_CLAIM_FILTER_FIELDS.patientName.placeholder,
			},
		},
		{
			...FieldTypeConfigs.text,
			field: filterEraClaimDataKeys.patientId,
			display_name: ERA_CLAIM_FILTER_FIELDS.patientId.label,
			isSearchable: true,
			custom_props: {
				placeholder: ERA_CLAIM_FILTER_FIELDS.patientId.placeholder,
			},
		},
		{
			...FieldTypeConfigs.number,
			field: filterEraClaimDataKeys.claimAmount,
			display_name: ERA_CLAIM_FILTER_FIELDS.billed.label,
			isSearchable: true,
			custom_props: {
				prefix: '$',
				type: 'number',
				placeholder: ERA_CLAIM_FILTER_FIELDS.billed.placeholder,
				stringifiedValueRenderer: (value: number) => `$${value}`,
			},
		},
	];
};

// get reconciled status
const getReconciledStatus = (
	data: ERA.ERADetailsResponse | ERA.ERAListResponse,
): { showReconcileButton: boolean; showReconciledTag: boolean } => {
	const status = data?.fhirResource?.status;
	const outcome = data?.fhirResource?.outcome;
	return {
		showReconcileButton: status === PAYMENT_RECONCILIATION_STATUS.active && outcome === PAYMENT_RECONCILIATION_OUTCOME.queued,
		showReconciledTag: status === PAYMENT_RECONCILIATION_STATUS.active && outcome === PAYMENT_RECONCILIATION_OUTCOME.complete,
	};
};

// prepare era criteria
const prepareCriteria = (activeTab: ClaimRequest.IGroupedClaimWorkState, criteria?: JsonTree): JsonTree => {
	if (!activeTab) {
		return criteria;
	}
	const claimStatusRule = getWorklistRule(activeTab);
	return addRuleToQuery(criteria, claimStatusRule);
};

// prepare era batch posting criteria
const prepareEraBatchPostingCriteria = (activeTab: ClaimRequest.IGroupedClaimWorkState, criteria: JsonTree): JsonTree => {
	if (activeTab !== GROUPED_CLAIM_WORK_STATES.active.groupValue) {
		return criteria;
	} else {
		const claimStatusRule = prepareQueryRule({
			field: filterDataKeys.workState,
			operator: OPERATORS.multiselectContains,
			value: [[CLAIM_WORK_STATES.a_adjudicated.value]],
		});
		return addRuleToQuery(criteria, claimStatusRule);
	}
};

export {
	filterDataKeys,
	getEraFilterSchema,
	getProviderAdjustmentMeta,
	getEraClaimFilterSchema,
	getReconciledStatus,
	prepareCriteria,
	prepareEraBatchPostingCriteria,
};
